import dayjs from 'dayjs';
import 'dayjs/locale/pt-br';
import relativeTime from 'dayjs/plugin/relativeTime';
import React, { useCallback, useMemo, useState } from 'react';
import { FileManagerEndpoints } from '../../../../api/endpoints/FileManagerEndpoints';
import { AttachmentType } from '../../../enums/AttachmentType';
import { type AttachmentDto } from '../../../models/AttachmentDto';
import BaseSearchFilter from './filters/BaseSearchFilter';
import DataTableBody from './DataTableBody';
import DataTablePagination from './DataTablePagination';
import ShowTotalFiles from './ShowTotalFiles';
import { type DataTableColumnDef } from '../../../models/DataTableColumnDef';
import { DataTable } from '../DataTable';
import SafeImage from '../../widgets/SafeImage';
import { type CellContext } from '@tanstack/react-table';

dayjs.extend(relativeTime);
dayjs.locale('pt-br');

type FilesLibraryProps = {
  onChange: (attachments?: AttachmentDto[]) => void;
  initialSelectedAttachments?: AttachmentDto[];
  maxQuantity?: number;
  allowedExtensions?: string[];
  onlyImage?: boolean;
};

const SelectButton: React.FC<{ selected: boolean; onClick: () => void }> = ({ selected, onClick }) => (
  <button
    className={`btn ${selected ? 'btn-primary' : 'btn-light-secondary'} btn-sm`}
    style={{
      paddingLeft: selected ? '10px' : '15px',
      paddingRight: selected ? '10px' : '15px',
      transition: 'all 0.3s ease',
    }}
    type="button"
    onClick={onClick}
  >
    {selected ? 'Selecionado' : 'Selecionar'}
  </button>
);

const FilesLibrary: React.FC<FilesLibraryProps> = ({
  onChange,
  initialSelectedAttachments = [],
  maxQuantity = -1,
  allowedExtensions,
  onlyImage = true,
}) => {
  const [selectedRows, setSelectedRows] = useState<AttachmentDto[]>(initialSelectedAttachments);

  const handleSelectToggle = useCallback(
    (attachment: AttachmentDto) => {
      setSelectedRows((prev) => {
        const isSelected = prev.some((item) => item.id === attachment.id);
        if (isSelected) {
          const updated = prev.filter((item) => item.id !== attachment.id);
          onChange(updated);
          return updated;
        } else if (prev.length === maxQuantity) {
          const filesWithoutFirst = prev.slice(1);
          const updated = [...filesWithoutFirst, attachment];
          onChange(updated);
          return updated;
        } else if (maxQuantity < 0 || prev.length < maxQuantity) {
          const updated = [...prev, attachment];
          onChange(updated);
          return updated;
        }
        return prev;
      });
    },
    [onChange, maxQuantity],
  );

  const actionCell = useCallback(
    ({ row }: CellContext<AttachmentDto, unknown>) => {
      const isSelected = selectedRows.some((item) => item.id === row.original.id);
      return (
        <div className="h-50px d-flex flex-column justify-content-center align-items-center">
          <SelectButton selected={isSelected} onClick={() => handleSelectToggle(row.original)} />
        </div>
      );
    },
    [selectedRows, handleSelectToggle],
  );

  const columns: DataTableColumnDef<AttachmentDto>[] = [
    {
      id: 'name',
      accessorKey: 'name',
      cell: ({ row }) => {
        const registrationDate = dayjs(row.original.registration_date);
        const timeAgo = registrationDate.fromNow(true);
        const isImage = row.original.type === AttachmentType.IMAGE;
        const url = row.original.public_url;

        return (
          <div className="d-flex" style={{ maxWidth: '300px' }}>
            <div className="d-flex flex-column justify-content-center align-items-center">
              {isImage ? (
                <SafeImage alt={row.original.name} className="rounded" height={36} src={url} width={36} />
              ) : (
                <i className="ki-solid ki-file fs-4x">
                  <span className="path2" />
                </i>
              )}
            </div>
            <div className="d-flex flex-column ps-4 justify-content-center" style={{ maxWidth: '200px' }}>
              <div className="text-gray-800 fw-bold text-truncate" style={{ maxWidth: '200px' }}>
                {row.original.name}
              </div>
              <div className="text-gray-500 fw-bold">{timeAgo} atrás</div>
            </div>
          </div>
        );
      },
    },
    {
      id: 'size',
      cell: ({ row }) => {
        const sizeInKB = (row.original.size / 1000).toFixed(0);
        return (
          <div className="text-gray-700 w-50px h-50px d-flex flex-column justify-content-center align-items-center text-nowrap">
            <div className="border border-1 rounded px-3 py-1">
              <b>{sizeInKB} KB</b>
            </div>
          </div>
        );
      },
    },
    {
      id: 'actions',
      cell: actionCell,
    },
  ];

  const libraryFilter = useMemo(
    () => ({
      extensions: allowedExtensions,
      ...(onlyImage ? { type: AttachmentType.IMAGE } : {}),
    }),
    [allowedExtensions, onlyImage],
  );

  return (
    <div>
      <div className="d-flex flex-column">
        <DataTable
          columns={columns as DataTableColumnDef<object>[]}
          endpoint={FileManagerEndpoints.list}
          filter={libraryFilter}
          hasMinHeight={false}
          perPage={5}
          showControls={false}
          showSelectedRows={false}
          tableKey="files-library"
        >
          <div className="d-flex justify-content-between">
            <ShowTotalFiles />
            <BaseSearchFilter />
          </div>

          <DataTableBody showHeader={false} />
          <DataTablePagination showPerPage={false} />
        </DataTable>
      </div>
    </div>
  );
};

export default FilesLibrary;
